import React, { useRef } from 'react';
import { Card, Row, Col, Timeline, Tag, Progress } from 'antd';
import { motion } from 'framer-motion';
import { 
  GlobalOutlined,
  TrophyOutlined,
  TeamOutlined,
  RocketOutlined,
  SafetyCertificateOutlined,
  ApiOutlined,
  BankOutlined,
  ToolOutlined
} from '@ant-design/icons';

const CompanyStrength: React.FC = () => {
  const mapRef = useRef<HTMLDivElement>(null);

  // 模拟全球网络节点数据
  const networkNodes = [
    { city: '深圳', country: '中国', type: 'headquarters', x: 78, y: 45 },
    { city: '香港', country: '中国', type: 'hub', x: 80, y: 47 },
    { city: '洛杉矶', country: '美国', type: 'major', x: 15, y: 35 },
    { city: '芝加哥', country: '美国', type: 'major', x: 22, y: 32 },
    { city: '纽约', country: '美国', type: 'major', x: 28, y: 33 },
    { city: '旧金山', country: '美国', type: 'major', x: 12, y: 37 },
    { city: '迈阿密', country: '美国', type: 'regional', x: 26, y: 45 },
    { city: '西雅图', country: '美国', type: 'regional', x: 14, y: 28 }
  ];

  const companyMilestones = [
    {
      year: '2018',
      title: '公司成立',
      description: '专注美国空派专线服务',
      icon: <BankOutlined />
    },
    {
      year: '2019',
      title: '四大机场布局',
      description: 'LAX/SFO/JFK/ORD清关网络建立',
      icon: <GlobalOutlined />
    },
    {
      year: '2020',
      title: '技术升级',
      description: '自主研发物流管理系统',
      icon: <ToolOutlined />
    },
    {
      year: '2022',
      title: '服务突破',
      description: '0.5kg起收，超时赔付服务推出',
      icon: <RocketOutlined />
    },
    {
      year: '2024',
      title: '行业领先',
      description: '97.2%清关成功率，客户满意度98.5%',
      icon: <TrophyOutlined />
    }
  ];

  const coreCapabilities = [
    {
      title: '全链条API对接',
      description: '自主研发ERP系统，实现物流全链条数据互通',
      icon: <ApiOutlined />,
      color: '#1890ff',
      percentage: 95
    },
    {
      title: '智能舱位管理',
      description: 'AI算法优化舱位配置，提升装载效率30%',
      icon: <RocketOutlined />,
      color: '#52c41a',
      percentage: 88
    },
    {
      title: '风险管控系统',
      description: '全方位风险防控体系，货物安全保障99.8%',
      icon: <SafetyCertificateOutlined />,
      color: '#fa8c16',
      percentage: 99
    },
    {
      title: '专业团队服务',
      description: '平均从业10年+经验，24小时专业客服',
      icon: <TeamOutlined />,
      color: '#722ed1',
      percentage: 92
    }
  ];

  return (
    <section style={{ 
      padding: '80px 0', 
      background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
      color: 'white',
      position: 'relative'
    }}>
      <div className="container">
        {/* 标题 */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ textAlign: 'center', marginBottom: '60px' }}
        >
          <h2 style={{ 
            fontSize: '2.5rem',
            fontWeight: '700',
            marginBottom: '24px',
            color: 'white'
          }}>
            企业实力展示
          </h2>
          <p style={{ 
            fontSize: '1.125rem',
            color: 'rgba(255, 255, 255, 0.9)',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            基于技术创新和专业服务，打造美国空派专线行业标杆
          </p>
        </motion.div>

        {/* 全球网络地图 */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          style={{ marginBottom: '80px' }}
        >
          <Card
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <GlobalOutlined style={{ color: '#1890ff', fontSize: '24px' }} />
                <span style={{ fontSize: '20px', fontWeight: '600' }}>全球服务网络布局</span>
              </div>
            }
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              border: 'none',
              borderRadius: '16px'
            }}
          >
            <div 
              ref={mapRef}
              style={{
                position: 'relative',
                height: '400px',
                background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
                borderRadius: '12px',
                overflow: 'hidden'
              }}
            >
              {/* 世界地图背景 */}
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                fontSize: '200px',
                color: 'rgba(24, 144, 255, 0.1)',
                zIndex: 1
              }}>
                🌍
              </div>

              {/* 网络节点 */}
              {networkNodes.map((node, index) => (
                <motion.div
                  key={index}
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  style={{
                    position: 'absolute',
                    left: `${node.x}%`,
                    top: `${node.y}%`,
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2
                  }}
                >
                  <div style={{
                    width: node.type === 'headquarters' ? '20px' : node.type === 'major' ? '16px' : '12px',
                    height: node.type === 'headquarters' ? '20px' : node.type === 'major' ? '16px' : '12px',
                    borderRadius: '50%',
                    background: node.type === 'headquarters' ? '#ff4d4f' : node.type === 'major' ? '#1890ff' : '#52c41a',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                    cursor: 'pointer',
                    animation: 'pulse 2s infinite'
                  }} />
                  
                  <div style={{
                    position: 'absolute',
                    top: '120%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    background: 'rgba(0,0,0,0.8)',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    whiteSpace: 'nowrap',
                    opacity: 0,
                    transition: 'opacity 0.3s',
                    pointerEvents: 'none'
                  }}
                  className="node-tooltip"
                  >
                    {node.city}
                  </div>
                </motion.div>
              ))}

              {/* 连接线动画 */}
              <svg style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 1
              }}>
                <defs>
                  <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(24, 144, 255, 0.3)" />
                    <stop offset="100%" stopColor="rgba(24, 144, 255, 0.8)" />
                  </linearGradient>
                </defs>
                
                {/* 示例连接线 - 从深圳到各个美国城市 */}
                <motion.line
                  x1="78%" y1="45%" x2="15%" y2="35%"
                  stroke="url(#lineGradient)"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 2, delay: 0.5 }}
                />
                <motion.line
                  x1="78%" y1="45%" x2="22%" y2="32%"
                  stroke="url(#lineGradient)"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 2, delay: 0.7 }}
                />
                <motion.line
                  x1="78%" y1="45%" x2="28%" y2="33%"
                  stroke="url(#lineGradient)"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 2, delay: 0.9 }}
                />
              </svg>

              {/* 网络统计 */}
              <div style={{
                position: 'absolute',
                bottom: '20px',
                left: '20px',
                background: 'rgba(255, 255, 255, 0.9)',
                padding: '16px',
                borderRadius: '8px',
                zIndex: 3
              }}>
                <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px' }}>
                  🌐 网络覆盖
                </div>
                <Row gutter={[16, 8]} style={{ fontSize: '12px' }}>
                  <Col span={12}>
                    <span style={{ color: '#ff4d4f' }}>● </span>总部基地: 1个
                  </Col>
                  <Col span={12}>
                    <span style={{ color: '#1890ff' }}>● </span>主要枢纽: 4个
                  </Col>
                  <Col span={12}>
                    <span style={{ color: '#52c41a' }}>● </span>区域网点: 3个
                  </Col>
                  <Col span={12}>
                    <strong>覆盖全美50州</strong>
                  </Col>
                </Row>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* 发展历程和核心能力 */}
        <Row gutter={[40, 40]}>
          {/* 发展历程 */}
          <Col xs={24} lg={12}>
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Card
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <TrophyOutlined style={{ color: '#faad14', fontSize: '24px' }} />
                    <span style={{ fontSize: '20px', fontWeight: '600' }}>发展历程</span>
                  </div>
                }
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: 'none',
                  borderRadius: '16px',
                  height: '100%'
                }}
              >
                <Timeline mode="left">
                  {companyMilestones.map((milestone, index) => (
                    <Timeline.Item
                      key={index}
                      dot={
                        <div style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #1890ff 0%, #722ed1 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '16px'
                        }}>
                          {milestone.icon}
                        </div>
                      }
                    >
                      <div style={{ paddingLeft: '16px' }}>
                        <Tag color="blue" style={{ marginBottom: '8px', fontSize: '12px' }}>
                          {milestone.year}
                        </Tag>
                        <h4 style={{ 
                          margin: '0 0 8px 0', 
                          fontSize: '16px', 
                          fontWeight: '600',
                          color: '#1D1D1F'
                        }}>
                          {milestone.title}
                        </h4>
                        <p style={{ 
                          margin: 0, 
                          color: '#666', 
                          fontSize: '14px',
                          lineHeight: '1.5'
                        }}>
                          {milestone.description}
                        </p>
                      </div>
                    </Timeline.Item>
                  ))}
                </Timeline>
              </Card>
            </motion.div>
          </Col>

          {/* 核心能力 */}
          <Col xs={24} lg={12}>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <Card
                title={
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <RocketOutlined style={{ color: '#52c41a', fontSize: '24px' }} />
                    <span style={{ fontSize: '20px', fontWeight: '600' }}>核心技术能力</span>
                  </div>
                }
                style={{
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: 'none',
                  borderRadius: '16px',
                  height: '100%'
                }}
              >
                {coreCapabilities.map((capability, index) => (
                  <div key={index} style={{ marginBottom: index < coreCapabilities.length - 1 ? '32px' : 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${capability.color}20, ${capability.color}40)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '20px',
                        color: capability.color
                      }}>
                        {capability.icon}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ 
                          margin: '0 0 4px 0', 
                          fontSize: '16px', 
                          fontWeight: '600',
                          color: '#1D1D1F'
                        }}>
                          {capability.title}
                        </h4>
                        <p style={{ 
                          margin: 0, 
                          color: '#666', 
                          fontSize: '13px',
                          lineHeight: '1.4'
                        }}>
                          {capability.description}
                        </p>
                      </div>
                    </div>
                    
                    <Progress
                      percent={capability.percentage}
                      strokeColor={{
                        '0%': capability.color,
                        '100%': capability.color + '80'
                      }}
                      trailColor="rgba(0,0,0,0.06)"
                      strokeWidth={8}
                      showInfo={false}
                      style={{ marginBottom: '8px' }}
                    />
                    <div style={{ textAlign: 'right', fontSize: '12px', color: capability.color, fontWeight: '600' }}>
                      {capability.percentage}% 行业领先水平
                    </div>
                  </div>
                ))}
              </Card>
            </motion.div>
          </Col>
        </Row>
      </div>

      {/* CSS动画 */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.2); opacity: 0.8; }
        }
        
        .node-tooltip {
          opacity: 0 !important;
          transition: opacity 0.3s;
        }
        
        .node-tooltip:hover {
          opacity: 1 !important;
        }
        
        [style*="cursor: pointer"]:hover + .node-tooltip {
          opacity: 1 !important;
        }
      `}</style>

      {/* 背景装饰 */}
      <div style={{
        position: 'absolute',
        top: '20%',
        right: '0%',
        width: '300px',
        height: '300px',
        background: 'radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        zIndex: 1,
        overflow: 'hidden'
      }} />
    </section>
  );
};

export default CompanyStrength;
